import { apiRequest, apiRequestBlob, apiUpload } from '@/lib/api/client';
import type {
  PreliminaryAnswerItem,
  WorkRequestDetail,
  WorkRequestListResponse,
} from './types';

export const getWorkRequests = (params?: {
  filter?: string;
  search?: string;
  sort?: string;
  kind?: string;
  role_context?: string;
}): Promise<WorkRequestListResponse> =>
  apiRequest<WorkRequestListResponse>('/work-requests', {
    channel: 'platform',
    query: {
      filter: params?.filter,
      search: params?.search,
      sort: params?.sort,
      kind: params?.kind,
      role_context: params?.role_context,
    },
  });

export const getWorkRequestDetail = (
  idOrCode: string,
  roleContext?: string
): Promise<WorkRequestDetail> =>
  apiRequest<WorkRequestDetail>(`/work-requests/${encodeURIComponent(idOrCode)}`, {
    channel: 'platform',
    query: {
      role_context: roleContext,
    },
  });

export const createWorkRequest = (
  payload: {
    title: string;
    service_name: string;
    direction?: string;
    kind?: string;
    priority?: string;
    answers?: Array<{ question: string; answer: string; status?: string; source?: string }>;
    due_at?: string | null;
    assignee?: string | null;
    related_project_id?: string | null;
    related_project_code?: string | null;
  },
  roleContext?: string
): Promise<WorkRequestDetail> =>
  apiRequest<WorkRequestDetail>('/work-requests', {
    method: 'POST',
    body: payload,
    channel: 'platform',
    query: {
      role_context: roleContext,
    },
  });

export const updateWorkRequestStatus = (
  idOrCode: string,
  status: string,
  reason?: string,
  roleContext?: string
): Promise<WorkRequestDetail> =>
  apiRequest<WorkRequestDetail>(`/work-requests/${encodeURIComponent(idOrCode)}/status`, {
    method: 'POST',
    body: { status, reason },
    channel: 'platform',
    query: {
      role_context: roleContext,
    },
  });

export const assignWorkRequest = (
  idOrCode: string,
  assignee: string,
  assignedBy?: string,
  roleContext?: string
): Promise<WorkRequestDetail> =>
  apiRequest<WorkRequestDetail>(`/work-requests/${encodeURIComponent(idOrCode)}/assign`, {
    method: 'POST',
    body: { assignee, assigned_by: assignedBy },
    channel: 'platform',
    query: {
      role_context: roleContext,
    },
  });

export const requestClarification = (
  idOrCode: string,
  payload: {
    question: string;
    recipient_id?: string;
    recipient_role?: string;
    deadline?: string;
  },
  roleContext?: string
): Promise<WorkRequestDetail> =>
  apiRequest<WorkRequestDetail>(`/work-requests/${encodeURIComponent(idOrCode)}/clarification`, {
    method: 'POST',
    body: payload,
    channel: 'platform',
    query: {
      role_context: roleContext,
    },
  });

export const answerClarification = (
  idOrCode: string,
  clarificationId: string,
  payload: {
    answer: string;
    file_attachment?: string;
  },
  roleContext?: string
): Promise<WorkRequestDetail> =>
  apiRequest<WorkRequestDetail>(
    `/work-requests/${encodeURIComponent(idOrCode)}/clarification/${encodeURIComponent(clarificationId)}/answer`,
    {
      method: 'POST',
      body: payload,
      channel: 'platform',
      query: {
        role_context: roleContext,
      },
    }
  );

export const updateInternalNote = (
  idOrCode: string,
  internalNotes: string,
  roleContext?: string
): Promise<WorkRequestDetail> =>
  apiRequest<WorkRequestDetail>(`/work-requests/${encodeURIComponent(idOrCode)}/internal-note`, {
    method: 'POST',
    body: { internal_notes: internalNotes },
    channel: 'platform',
    query: {
      role_context: roleContext,
    },
  });

export const addWorkRequestFile = (
  idOrCode: string,
  payload: {
    filename: string;
    version?: string;
    status?: string;
    filesize_label?: string;
    stage?: string;
    url?: string;
  },
  roleContext?: string
): Promise<WorkRequestDetail> =>
  apiRequest<WorkRequestDetail>(`/work-requests/${encodeURIComponent(idOrCode)}/files`, {
    method: 'POST',
    body: payload,
    channel: 'platform',
    query: {
      role_context: roleContext,
    },
  });

export const submitWorkRequestResult = (
  idOrCode: string,
  payload: {
    summary: string;
    files?: Array<{ filename: string; file_id?: string | null; version_id?: string | null; size?: string | number | null; url?: string | null }>;
  },
  roleContext?: string
): Promise<WorkRequestDetail> =>
  apiRequest<WorkRequestDetail>(`/work-requests/${encodeURIComponent(idOrCode)}/result`, {
    method: 'POST',
    body: payload,
    channel: 'platform',
    query: {
      role_context: roleContext,
    },
  });

export const confirmWorkRequestResult = (
  idOrCode: string,
  roleContext?: string
): Promise<WorkRequestDetail> =>
  apiRequest<WorkRequestDetail>(`/work-requests/${encodeURIComponent(idOrCode)}/confirm-result`, {
    method: 'POST',
    channel: 'platform',
    query: {
      role_context: roleContext,
    },
  });

export const cancelWorkRequest = (
  idOrCode: string,
  reason?: string,
  roleContext?: string
): Promise<WorkRequestDetail> =>
  apiRequest<WorkRequestDetail>(`/work-requests/${encodeURIComponent(idOrCode)}/cancel`, {
    method: 'POST',
    channel: 'platform',
    query: {
      reason,
      role_context: roleContext,
    },
  });

export const getPreliminaryAnswers = (params?: {
  status?: string;
  search?: string;
  role_context?: string;
}): Promise<PreliminaryAnswerItem[]> =>
  apiRequest<PreliminaryAnswerItem[]>('/work-requests/preliminary/items', {
    channel: 'platform',
    query: {
      status: params?.status,
      search: params?.search,
      role_context: params?.role_context,
    },
  });

export const updatePreliminaryStatus = (
  preliminaryId: string,
  status: string,
  roleContext?: string
): Promise<PreliminaryAnswerItem> =>
  apiRequest<PreliminaryAnswerItem>(
    `/work-requests/preliminary/${encodeURIComponent(preliminaryId)}/status`,
    {
      method: 'POST',
      body: { status },
      channel: 'platform',
      query: {
        role_context: roleContext,
      },
    }
  );


export const uploadWorkRequestFile = (id: string, file: File, version = 'v1', roleContext?: string): Promise<WorkRequestDetail> =>
  apiUpload<WorkRequestDetail>(`/work-requests/${encodeURIComponent(id)}/files/upload${roleContext ? `?role_context=${encodeURIComponent(roleContext)}` : ''}`, file, 'file', { version });

export const downloadWorkRequestFile = async (path: string, filename: string, roleContext?: string): Promise<void> => {
  if (!path.startsWith('/work-requests/') && !path.startsWith('/file-hub/')) throw new Error('Файл не связан с хранилищем заявки');
  const urlPath = path + (roleContext ? `${path.includes('?') ? '&' : '?'}role_context=${encodeURIComponent(roleContext)}` : '');
  const result = await apiRequestBlob(urlPath, { channel: 'platform' });
  const url = URL.createObjectURL(result.blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
};
